import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import tw from "twin.macro";
import { pages, pageTypes } from "..";
import { DP } from "../../../common/types";
import Spinner from "../../../components/Spinner";
import SpinnerDots from "../../../components/SpinnerDots";
import { createCustomStore } from "../../../context/customStore";
import Post from "../../../sections/Post";
import { IPost } from "../../../sections/Post/types";
import { fetchGetPosts } from "../api";
import { useTopicsValue } from "./Topics";

interface IStore {
  posts: IPost[];
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  totalPages?: number;
	currentPage: pageTypes
}

interface Props extends DP {}

export const {
  useValue: usePostsStore,
  setOne: setPostsStoreField,
  set: setPostsStore,
  get: getPostsStore,
} = createCustomStore<IStore>({
  isLoading: true,
  isFetching: true,
  page: 0,
  posts: [],
	currentPage: 'home'
});

const Posts = (props: Props) => {
  const posts = usePostsStore((s) => s.posts);
  const isLoading = usePostsStore((s) => s.isLoading);
  const isFetching = usePostsStore((s) => s.isFetching);
  const searchingTopics = useTopicsValue((s) => s.searchingTopics);
  const { totalPages, page: currentPage } = getPostsStore();

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = (e) => {
    const scrollBottom = e.target.scrollTop + window.innerHeight;
    if (
      e.target.scrollHeight - scrollBottom < 500 &&
      !isFetching &&
      currentPage < (totalPages ?? 2)
    ) {
      setPostsStoreField("page", (v) => v + 1);
      setPostsStore({ isFetching: true });
    }
  };

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (wrapperRef.current)
        wrapperRef.current.removeEventListener("scroll", handleScroll);
    };
  }, [wrapperRef.current, isFetching]);

  useEffect(() => {
    if (isFetching) {
      fetchGetPosts();
    }
  }, [isFetching]);

  useEffect(() => {
    if (
      searchingTopics.length == 1 ||
      (!searchingTopics.length && !isFetching)
    ) {
      setPostsStore({ posts: [], isLoading: true, page: 0 });
      fetchGetPosts();
    }
  }, [searchingTopics]);

  if (isLoading) return <PostsSpinner className={props.className} />;
  if (!posts || !posts.length) return null;

  return (
    <Wrapper ref={wrapperRef} className={props.className}>
      {posts.map((el) => (
        <Post key={el._id} post={el} />
      ))}
      {isFetching ? <SpinnerDots /> : !posts.length && <>Not found</>}
    </Wrapper>
  );
};

const Wrapper = styled.div(() => [
  tw`flex flex-col items-center px-[1rem] overflow-y-auto`,
  css`
    ${Post}:first-child {
      padding: 1rem 0;
    }
    ${Post}:last-child {
      margin-bottom: 0.5rem;
    }
    ${Post} + ${Post} {
      border-top: var(--hr-border);
      padding: 1rem 0;
    }
    ${SpinnerDots} {
      ${tw`mt-2 mb-6`}
    }
  `,
]);

const PostsSpinner = styled(Spinner)(() => [
  tw`translate-y-[-3rem] md:(translate-x-[-2rem])`,
]);

export default styled(Posts)``;
